import React, { useEffect, useRef } from 'react'
import { Link, Redirect, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import SecretarioLayout, { START_HREF } from '../secretario/layout'
import { ConnectorLogo, PromptExample, StatusBadge, VideoPlayer } from '../secretario/components'
import { AVAILABLE, CATEGORIES, PLANNED, categoryOf, connectorsOf, findConnector } from '../secretario/catalog'
import Icon from '../components/icon'
import { SITE, waLink } from '../site'

// Pestaña de lo que está en la hoja de ruta; su URL es /secretario/aprende/proximamente.
const PLANNED_TAB = 'proximamente'

// Las pestañas cambian la URL sin mover el scroll (ver useScrollOnNavigate).
const tabLink = (id) => ({ pathname: `/secretario/aprende/${id}`, state: { keepScroll: true } })

const TABS = CATEGORIES.filter((c) => connectorsOf(c.id).length > 0)

const ExternalOrInternal = ({ href, children }) =>
  href.startsWith('/') ? (
    <Link to={href}>{children}</Link>
  ) : (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children} <Icon name="external" size={14} />
    </a>
  )

// Cómo se usa cualquier herramienta, con un ejemplo real del conector.
const usageSteps = (connector) => {
  const action = connector.tools.find((t) => t.effect) || connector.tools[0]
  return [
    { title: 'Pídelo con tus palabras', text: `Por mensaje o nota de voz, por ejemplo: «${action.examples[0]}».` },
    { title: 'Revisa la propuesta', text: 'Si la acción cambia algo, Secretario te cuenta exactamente qué va a hacer. Si quieres un ajuste, díselo y te la vuelve a proponer.' },
    { title: 'Confirma con «sí»', text: 'Responde «sí», «dale» o «listo» en el siguiente mensaje y Secretario lo hace. Las consultas las responde sin pedir confirmación.' },
  ]
}

// Plantilla de un conector. Nivel 1 (descubrimiento) a la vista; nivel 2 (aprendizaje) desplegable.
const ConnectorGuide = ({ connector }) => {
  const category = categoryOf(connector)
  const firstExample = connector.tools[0].examples[0]

  return (
    <article className="sec-guide" aria-labelledby={`guia-${connector.id}`}>
      <header className="sec-guide__head">
        <ConnectorLogo connector={connector} size={64} />
        <div className="sec-guide__title">
          <span className="sec-guide__cat">{category.name}</span>
          <h2 id={`guia-${connector.id}`}>{connector.name}</h2>
          <p>{connector.summary}</p>
        </div>
        <div className="sec-guide__cta">
          <StatusBadge status={connector.status} />
          <a href={waLink(firstExample)} className="sec-btn sec-btn--accent sec-btn--sm" target="_blank" rel="noopener noreferrer">
            <Icon name="send" size={16} /> Probar en WhatsApp
          </a>
        </div>
      </header>

      <div className="sec-guide__top">
        <VideoPlayer video={connector.video} fallbackTitle={`Cómo usar ${connector.name} con Secretario`} />
        <div className="sec-guide__can">
          <h3>Qué puedes hacer</h3>
          <ul className="sec-checks">
            {connector.capabilities.map((c) => (
              <li key={c}>
                <Icon name="check" size={16} />
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <section className="sec-guide__tools" aria-labelledby={`herramientas-${connector.id}`}>
        <h3 id={`herramientas-${connector.id}`}>
          Herramientas disponibles <span className="sec-count">{connector.tools.length}</span>
        </h3>
        <div className="sec-tools">
          {connector.tools.map((t) => (
            <div key={t.id} className="sec-tool">
              <div className="sec-tool__head">
                <h4>{t.name}</h4>
                <span className={`sec-tag${t.effect ? ' sec-tag--confirm' : ''}`}>
                  {t.effect ? 'Pide tu «sí»' : 'Consulta'}
                </span>
              </div>
              <p>{t.description}</p>
              <ul className="sec-prompts" aria-label={`Ejemplos para ${t.name}`}>
                {t.examples.map((e) => (
                  <PromptExample key={e} text={e} />
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="sec-auth-note">
          <Icon name="info" size={18} />
          <span>
            «Probar» abre WhatsApp con la instrucción escrita para Secretario. Solo responde a números registrados.{' '}
            <Link to={START_HREF}>¿Aún no tienes acceso? Solicítalo aquí.</Link>
          </span>
        </p>
      </section>

      <details className="sec-deep">
        <summary>
          <span>
            <strong>Guía paso a paso</strong>
            <small>Cómo conectarlo, requisitos, permisos y consideraciones</small>
          </span>
          <Icon name="chevron" size={20} />
        </summary>
        <div className="sec-deep__body">
          <div className="sec-deep__block">
            <h4>Cómo conectarlo</h4>
            <ol className="sec-numbered">
              {connector.setup.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </div>
          <div className="sec-deep__block">
            <h4>Cómo usar las herramientas</h4>
            <ol className="sec-numbered">
              {usageSteps(connector).map((s) => (
                <li key={s.title}>
                  <strong>{s.title}.</strong> {s.text}
                </li>
              ))}
            </ol>
          </div>
          <div className="sec-deep__block">
            <h4>Requisitos</h4>
            <ul className="sec-bullets">
              {connector.requirements.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
          {connector.scopes.length > 0 && (
            <div className="sec-deep__block">
              <h4>Permisos que pide</h4>
              <ul className="sec-scopes">
                {connector.scopes.map((s) => (
                  <li key={s.scope}>
                    <strong>{s.access}</strong>
                    <code>{s.scope}</code>
                    <span>{s.use}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="sec-deep__block">
            <h4>Consideraciones importantes</h4>
            <ul className="sec-bullets">
              {connector.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </div>
          {connector.links.length > 0 && (
            <div className="sec-deep__block">
              <h4>Enlaces relacionados</h4>
              <ul className="sec-links">
                {connector.links.map((l) => (
                  <li key={l.href}>
                    <ExternalOrInternal href={l.href}>{l.label}</ExternalOrInternal>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </details>
    </article>
  )
}

const PlannedPanel = () => (
  <div className="sec-guide">
    <div className="sec-guide__title">
      <span className="sec-guide__cat">Próximamente</span>
      <h2>En nuestra hoja de ruta</h2>
      <p>Integraciones que estamos evaluando. Todavía no están disponibles y no tienen fecha confirmada.</p>
    </div>
    <div className="sec-planned">
      {PLANNED.map((c) => (
        <article key={c.id} className="sec-planned__item">
          <ConnectorLogo connector={c} size={40} />
          <div>
            <h3>{c.name}</h3>
            <p>{c.summary}</p>
          </div>
          <StatusBadge status={c.status} />
        </article>
      ))}
    </div>
  </div>
)

const Learn = () => {
  const { conector } = useParams()
  const tabsRef = useRef(null)

  // En pantallas angostas la barra de pestañas se desplaza: deja la activa a la vista.
  useEffect(() => {
    const nav = tabsRef.current
    const active = nav && nav.querySelector('.is-active')
    if (active) nav.scrollLeft = active.offsetLeft - (nav.clientWidth - active.clientWidth) / 2
  }, [conector])
  const showPlanned = conector === PLANNED_TAB && PLANNED.length > 0
  const connector = showPlanned ? null : conector ? findConnector(conector) : AVAILABLE[0]

  // Un conector que no existe o que aún no está disponible vuelve a la portada del centro de ayuda.
  if (!showPlanned && !connector) return <Redirect to="/secretario/aprende" />

  const activeCategory = connector ? connector.category : PLANNED_TAB
  const siblings = connector ? connectorsOf(connector.category) : []
  const path = conector ? `/secretario/aprende/${conector}` : '/secretario/aprende'
  const title = connector
    ? `Cómo usar ${connector.name} con Secretario · Centro de ayuda`
    : 'Próximamente en Secretario · Centro de ayuda'

  return (
    <SecretarioLayout>
      <Helmet>
        <title>{`${title} | Variant`}</title>
        <meta
          name="description"
          content={
            connector
              ? `${connector.summary} Herramientas, ejemplos para copiar y guía paso a paso de ${connector.name} en Secretario.`
              : 'Integraciones de Secretario que están en la hoja de ruta.'
          }
        />
        <link rel="canonical" href={`${SITE.url}${path}`} />
        <meta property="og:title" content={title} />
        <meta property="og:url" content={`${SITE.url}${path}`} />
      </Helmet>

      <section className="sec-page-head sec-page-head--compact">
        <div className="container">
          <span className="sec-eyebrow sec-eyebrow--light">Centro de ayuda</span>
          <h1 className="sec-page-head__title">Aprende a usar Secretario</h1>
          <p className="sec-page-head__lead">
            Elige una categoría y un conector: verás qué puede hacer, sus herramientas, ejemplos para copiar y una guía paso
            a paso.
          </p>
        </div>
      </section>

      <div className="sec-tabs-bar">
        <div className="container">
          <nav className="sec-tabs" ref={tabsRef} aria-label="Categorías del centro de ayuda">
            {TABS.map((c) => (
              <Link
                key={c.id}
                to={tabLink(connectorsOf(c.id)[0].id)}
                className={`sec-tab${activeCategory === c.id ? ' is-active' : ''}`}
                aria-current={activeCategory === c.id ? 'page' : undefined}
              >
                <Icon name={c.icon} size={18} />
                {c.name}
              </Link>
            ))}
            {PLANNED.length > 0 && (
              <Link
                to={tabLink(PLANNED_TAB)}
                className={`sec-tab${showPlanned ? ' is-active' : ''}`}
                aria-current={showPlanned ? 'page' : undefined}
              >
                <Icon name="clock" size={18} />
                Próximamente
              </Link>
            )}
          </nav>
        </div>
      </div>

      <section className="sec-section sec-section--tint sec-learn">
        <div className="container">
          {siblings.length > 1 && (
            <nav className="sec-subtabs" aria-label="Conectores de la categoría">
              {siblings.map((c) => (
                <Link
                  key={c.id}
                  to={tabLink(c.id)}
                  className={`sec-subtab${c.id === connector.id ? ' is-active' : ''}`}
                  aria-current={c.id === connector.id ? 'page' : undefined}
                >
                  <ConnectorLogo connector={c} size={26} />
                  {c.name}
                </Link>
              ))}
            </nav>
          )}
          {connector ? <ConnectorGuide key={connector.id} connector={connector} /> : <PlannedPanel />}
        </div>
      </section>
    </SecretarioLayout>
  )
}

export default Learn
